/**
 * Admin Settings — application-wide preferences.
 *
 * Changes made here apply to every visitor of the app (persisted to the
 * `app_settings` table via /api/settings.php). Adding new settings:
 *   1. Extend AppSettings in AppSettingsContext + server validators in
 *      settings.php.
 *   2. Add a new section below that reads/writes through updateSettings(),
 *      wrapped in the `{activeTab === '<tab>' && ( … )}` gate for whichever
 *      tab it belongs to (see SETTINGS_TABS). Sections are grouped into tabs
 *      and the active tab is mirrored in the URL (?tab=).
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings, RotateCcw, Loader2, Shield, MessageSquare, Send, Bug, BarChart3, RefreshCw, ShieldCheck, Plus, X, Video, MapPin, Clock, LogIn, Mail, SlidersHorizontal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { TURNSTILE_CONFIG } from '../../config/integrations';
import { useAppSettings, TUTORIAL_DEFAULTS_EN, type AppSettings } from '../../context/AppSettingsContext';
import { SPACE_TYPES } from '../../data/presets';
import { SPACE_USES, spaceAllowed } from '../../data/spaceAccess';
import { fetchGeoProviders, fetchGeocode, type GeoProviderMeta } from '../../services/api';
import { fetchMapFacets } from '../../services/adminLocationMapsApi';
import {
  fetchSmtpSettings, saveSmtpSettings, sendSmtpTest, SmtpSettings, SmtpSettingsPatch,
} from '../../services/api';
import {
  TRANSITION_OPTIONS,
  TransitionType,
  TRANSITION_DURATION_MIN_MS,
  TRANSITION_DURATION_MAX_MS,
  TRANSITION_DURATION_STEP_MS,
  CYCLE_INTERVAL_MIN_MS,
  CYCLE_INTERVAL_MAX_MS,
  CYCLE_INTERVAL_STEP_MS,
  clampTransitionDuration,
  clampCycleInterval,
} from '../../config/transitions';

/**
 * Available IANA timezones. Sourced from the browser's own list when supported
 * (no hardcoded catalog to maintain); falls back to a North-America-focused set
 * on older engines. The configured value is always merged in so a custom/legacy
 * value never disappears from the dropdown.
 */
const TIMEZONE_OPTIONS: string[] = (() => {
  try {
    const intlAny = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
    if (typeof intlAny.supportedValuesOf === 'function') {
      return intlAny.supportedValuesOf('timeZone');
    }
  } catch {
    /* fall through to the curated list */
  }
  return [
    'UTC',
    'America/St_Johns', 'America/Halifax', 'America/Toronto', 'America/New_York',
    'America/Winnipeg', 'America/Chicago', 'America/Edmonton', 'America/Denver',
    'America/Vancouver', 'America/Los_Angeles',
  ];
})();

/**
 * Settings are grouped into tabs so the page stays scannable. Each section in
 * the body is gated on `activeTab === <id>`; the active tab is mirrored in the
 * URL (?tab=) so a specific group is deep-linkable and survives a refresh.
 */
type SettingsTabId = 'general' | 'display' | 'integrations' | 'accounts';

/** Shown until geocode.php?action=providers answers (or when it cannot). */
const GEO_PROVIDER_FALLBACK: GeoProviderMeta[] = [
  { name: 'photon', label: 'Photon (OpenStreetMap)', keyless: true, freeTier: '', attribution: false, supportsDetails: false, needsSession: false, configured: true },
  { name: 'geoapify', label: 'Geoapify', keyless: false, freeTier: '', attribution: false, supportsDetails: false, needsSession: false, configured: false },
  { name: 'locationiq', label: 'LocationIQ', keyless: false, freeTier: '', attribution: 'Search by LocationIQ', supportsDetails: false, needsSession: false, configured: false },
  { name: 'radar', label: 'Radar', keyless: false, freeTier: '', attribution: false, supportsDetails: false, needsSession: false, configured: false },
  { name: 'here', label: 'HERE', keyless: false, freeTier: '', attribution: false, supportsDetails: false, needsSession: false, configured: false },
  { name: 'google', label: 'Google Places (New)', keyless: false, freeTier: '', attribution: false, supportsDetails: true, needsSession: true, configured: false },
  { name: 'nominatim', label: 'Nominatim (self-hosted only)', keyless: true, freeTier: '', attribution: false, supportsDetails: false, needsSession: false, configured: true },
];

const SETTINGS_TABS: { id: SettingsTabId; label: string; icon: LucideIcon }[] = [
  { id: 'general',      label: 'General',              icon: SlidersHorizontal },
  { id: 'display',      label: 'Estimator & Display',  icon: Video },
  { id: 'integrations', label: 'Integrations',         icon: Send },
  { id: 'accounts',     label: 'Accounts & Security',  icon: Shield },
];

export const AdminSettings: React.FC = () => {
  const { settings, isLoading, error, updateSettings, resetSettings } = useAppSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: SettingsTabId = SETTINGS_TABS.some(t => t.id === tabParam)
    ? (tabParam as SettingsTabId)
    : 'general';
  const goToTab = (id: SettingsTabId) => setSearchParams({ tab: id });
  const [saving, setSaving] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  // Local edit buffers for text inputs — commit on blur so every keystroke
  // doesn't fire a server write. Kept in sync with the server-owned values
  // via the effect below (handles external updates and initial hydration).
  const [pardotUrlDraft, setPardotUrlDraft] = useState(settings.pardotFormHandlerUrl);
  const [pardotFriendlyNameDraft, setPardotFriendlyNameDraft] = useState(settings.pardotFriendlyName);
  const [tutorialUrlDraft, setTutorialUrlDraft] = useState(settings.tutorialVideoUrl);
  const [tutorialMainTextDraft, setTutorialMainTextDraft] = useState(settings.tutorialMainLinkText);
  const [tutorialMenuTextDraft, setTutorialMenuTextDraft] = useState(settings.tutorialMenuLinkText);
  // Geocoding registry: per-provider write-only keys (start blank; only a new value is
  // ever sent), endpoint drafts (commit on blur), the provider roster from the server
  // (labels, free-tier notes, configured flags) and per-provider "Test" results.
  const [geoKeyDraft, setGeoKeyDraft] = useState<Record<string, string>>({});
  const [geoProviders, setGeoProviders] = useState<GeoProviderMeta[] | null>(null);
  const [geoTest, setGeoTest] = useState<Record<string, { status: 'busy' | 'ok' | 'err'; text: string }>>({});
  // Location maps: upstream URL / write-only key / tile style + public tile key drafts.
  const [mapsUpstreamDraft, setMapsUpstreamDraft] = useState(settings.mapsUpstreamBaseUrl);
  const [mapsKeyDraft, setMapsKeyDraft] = useState('');
  const [mapsStyleDraft, setMapsStyleDraft] = useState(settings.mapsDefaultTileStyleUrl);
  const [mapsTileKeyDraft, setMapsTileKeyDraft] = useState(settings.mapsTileApiKey);
  const [mapsTest, setMapsTest] = useState<{ status: 'busy' | 'ok' | 'err'; text: string } | null>(null);
  const [photonEndpointDraft, setPhotonEndpointDraft] = useState(settings.geoPhotonEndpoint);
  const [nominatimEndpointDraft, setNominatimEndpointDraft] = useState(settings.geoNominatimEndpoint);
  const [nominatimEmailDraft, setNominatimEmailDraft] = useState(settings.geoNominatimEmail);
  // SSO / Keycloak — issuer + client id mirror the server; secret is write-only.
  const [oidcIssuerDraft, setOidcIssuerDraft] = useState(settings.oidcIssuer);
  const [oidcClientIdDraft, setOidcClientIdDraft] = useState(settings.oidcClientId);
  const [oidcSecretDraft, setOidcSecretDraft] = useState('');
  // Gleap chat widget — public SDK key + excluded paths (one per line), commit on blur.
  const [gleapKeyDraft, setGleapKeyDraft] = useState(settings.gleapSdkKey);
  const [gleapExcludeDraft, setGleapExcludeDraft] = useState(settings.gleapExcludePaths.join('\n'));

  // SMTP / email — a dedicated super-admin endpoint (admin-smtp.php), not part
  // of the public AppSettings. Password is write-only.
  const [smtp, setSmtp] = useState<SmtpSettings | null>(null);
  const [smtpDraft, setSmtpDraft] = useState<SmtpSettingsPatch>({});
  const [smtpPasswordDraft, setSmtpPasswordDraft] = useState('');
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestTo, setSmtpTestTo] = useState('');
  const [smtpMsg, setSmtpMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    fetchSmtpSettings()
      .then((s) => {
        setSmtp(s);
        setSmtpDraft({
          smtpEnabled: s.smtpEnabled, smtpHost: s.smtpHost, smtpPort: s.smtpPort,
          smtpUser: s.smtpUser, smtpFrom: s.smtpFrom, smtpFromName: s.smtpFromName,
          smtpStartTls: s.smtpStartTls, smtpReplyTo: s.smtpReplyTo, smtpNoReplyNotice: s.smtpNoReplyNotice,
        });
      })
      .catch(() => { /* non-super-admins simply won't see saved values */ });
  }, []);

  const setSmtpField = <K extends keyof SmtpSettingsPatch>(k: K, v: SmtpSettingsPatch[K]) =>
    setSmtpDraft((d) => ({ ...d, [k]: v }));

  const smtpPatch = (): SmtpSettingsPatch => {
    const p: SmtpSettingsPatch = { ...smtpDraft };
    if (smtpPasswordDraft.trim()) p.smtpPassword = smtpPasswordDraft;
    return p;
  };

  const saveSmtp = async () => {
    setSmtpSaving(true); setSmtpMsg(null);
    try {
      const s = await saveSmtpSettings(smtpPatch());
      setSmtp(s);
      setSmtpPasswordDraft('');
      setSmtpMsg({ type: 'ok', text: 'Email settings saved.' });
    } catch (e) {
      setSmtpMsg({ type: 'err', text: e instanceof Error ? e.message : 'Could not save email settings.' });
    } finally {
      setSmtpSaving(false);
    }
  };

  const testSmtp = async () => {
    setSmtpTesting(true); setSmtpMsg(null);
    const res = await sendSmtpTest(smtpTestTo.trim(), smtpPatch());
    setSmtpTesting(false);
    setSmtpMsg(res.ok
      ? { type: 'ok', text: `Test email sent to ${smtpTestTo.trim()}.` }
      : { type: 'err', text: res.error || 'Test send failed.' });
  };

  useEffect(() => {
    setPardotUrlDraft(settings.pardotFormHandlerUrl);
  }, [settings.pardotFormHandlerUrl]);

  useEffect(() => {
    setPardotFriendlyNameDraft(settings.pardotFriendlyName);
  }, [settings.pardotFriendlyName]);

  useEffect(() => {
    setTutorialUrlDraft(settings.tutorialVideoUrl);
  }, [settings.tutorialVideoUrl]);

  useEffect(() => {
    setTutorialMainTextDraft(settings.tutorialMainLinkText);
  }, [settings.tutorialMainLinkText]);

  useEffect(() => {
    setTutorialMenuTextDraft(settings.tutorialMenuLinkText);
  }, [settings.tutorialMenuLinkText]);

  useEffect(() => {
    setOidcIssuerDraft(settings.oidcIssuer);
  }, [settings.oidcIssuer]);

  useEffect(() => {
    setOidcClientIdDraft(settings.oidcClientId);
  }, [settings.oidcClientId]);

  const runUpdate = async (patch: Parameters<typeof updateSettings>[0]) => {
    setSaving(true);
    setLastError(null);
    try {
      await updateSettings(patch);
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const commitPardotUrl = () => {
    const trimmed = pardotUrlDraft.trim();
    if (trimmed === settings.pardotFormHandlerUrl) return;
    runUpdate({ pardotFormHandlerUrl: trimmed });
  };

  const commitPardotFriendlyName = () => {
    const trimmed = pardotFriendlyNameDraft.trim();
    if (trimmed === '' || trimmed === settings.pardotFriendlyName) {
      // Empty not allowed server-side — revert to the last saved value.
      setPardotFriendlyNameDraft(settings.pardotFriendlyName);
      return;
    }
    runUpdate({ pardotFriendlyName: trimmed });
  };

  useEffect(() => { setGleapKeyDraft(settings.gleapSdkKey); }, [settings.gleapSdkKey]);
  useEffect(() => { setGleapExcludeDraft(settings.gleapExcludePaths.join('\n')); }, [settings.gleapExcludePaths]);

  const commitGleapKey = () => {
    const trimmed = gleapKeyDraft.trim();
    if (trimmed === settings.gleapSdkKey) return;
    runUpdate({ gleapSdkKey: trimmed });
  };

  const commitGleapExclude = () => {
    const paths = gleapExcludeDraft.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    if (paths.join('\n') === settings.gleapExcludePaths.join('\n')) return;
    runUpdate({ gleapExcludePaths: paths });
  };

  const reloadGeoProviders = () => {
    fetchGeoProviders().then((r) => { if (r) setGeoProviders(r.providers); }).catch(() => { /* roster is a convenience */ });
  };
  useEffect(() => { reloadGeoProviders(); }, []);
  useEffect(() => { setPhotonEndpointDraft(settings.geoPhotonEndpoint); }, [settings.geoPhotonEndpoint]);
  useEffect(() => { setNominatimEndpointDraft(settings.geoNominatimEndpoint); }, [settings.geoNominatimEndpoint]);
  useEffect(() => { setNominatimEmailDraft(settings.geoNominatimEmail); }, [settings.geoNominatimEmail]);

  useEffect(() => { setMapsUpstreamDraft(settings.mapsUpstreamBaseUrl); }, [settings.mapsUpstreamBaseUrl]);
  useEffect(() => { setMapsStyleDraft(settings.mapsDefaultTileStyleUrl); }, [settings.mapsDefaultTileStyleUrl]);
  useEffect(() => { setMapsTileKeyDraft(settings.mapsTileApiKey); }, [settings.mapsTileApiKey]);

  const commitMapsKey = async () => {
    const trimmed = mapsKeyDraft.trim();
    if (!trimmed) return;
    await runUpdate({ mapsUpstreamKey: trimmed });
    setMapsKeyDraft('');
  };

  const testMapsUpstream = async () => {
    setMapsTest({ status: 'busy', text: 'Asking trusscore.com for retailer facets…' });
    try {
      const r = await fetchMapFacets('retailers');
      if ('notConfigured' in r) { setMapsTest({ status: 'err', text: 'Endpoint URL or key is not set yet.' }); return; }
      const f = r.facets as { buyingGroups?: unknown[]; countries?: unknown[] };
      setMapsTest({ status: 'ok', text: `Connected: ${f.buyingGroups?.length ?? 0} buying groups, ${f.countries?.length ?? 0} countries${r.stale ? ' (stale cache — the live call failed)' : ''}.` });
    } catch (e) {
      setMapsTest({ status: 'err', text: `Failed: ${e instanceof Error ? e.message : 'unknown error'} — check the URL, the key (and Clear Cache on trusscore.com), and any Cloudflare bot rule.` });
    }
  };

  const geoKeyPatch = (provider: string, value: string): Partial<AppSettings> => {
    const patch: Partial<AppSettings> = {};
    (patch as Record<string, unknown>)[`geoKey_${provider}`] = value;
    return patch;
  };

  const commitGeoKey = async (provider: string) => {
    const trimmed = (geoKeyDraft[provider] ?? '').trim();
    if (!trimmed) return; // blank input = no change (use "Clear" to remove)
    await runUpdate(geoKeyPatch(provider, trimmed));
    setGeoKeyDraft(d => ({ ...d, [provider]: '' }));
    reloadGeoProviders();
  };

  const clearGeoKey = async (provider: string) => {
    await runUpdate(geoKeyPatch(provider, ''));
    setGeoKeyDraft(d => ({ ...d, [provider]: '' }));
    reloadGeoProviders();
  };

  const testGeoProvider = async (provider: string) => {
    setGeoTest(t => ({ ...t, [provider]: { status: 'busy', text: 'Querying "1 Main Street Toronto"…' } }));
    const r = await fetchGeocode({ q: '1 Main Street Toronto', provider, limit: 3, biasIp: false, countries: ['CA'] });
    if (r.error) {
      const why = r.error === 'not_configured' ? 'no key stored for this provider' : r.error === 'http_403' ? 'HTTP 403 — for Google, enable "Places API (New)" and make sure the key is not HTTP-referrer restricted' : r.error;
      setGeoTest(t => ({ ...t, [provider]: { status: 'err', text: `Failed: ${why}` } }));
      return;
    }
    const labels = r.predictions.slice(0, 3).map(p => p.label).join(' · ');
    const warn = r.warnings?.length ? ` (${r.warnings.join('; ')})` : '';
    setGeoTest(t => ({ ...t, [provider]: r.predictions.length
      ? { status: 'ok', text: `${r.provider} answered: ${labels}${warn}` }
      : { status: 'err', text: `${r.provider} returned no predictions${warn}` } }));
  };

  const commitOidcIssuer = () => {
    const trimmed = oidcIssuerDraft.trim();
    if (trimmed === settings.oidcIssuer) return;
    runUpdate({ oidcIssuer: trimmed });
  };

  const commitOidcClientId = () => {
    const trimmed = oidcClientIdDraft.trim();
    if (trimmed === settings.oidcClientId) return;
    runUpdate({ oidcClientId: trimmed });
  };

  const commitOidcSecret = async () => {
    const trimmed = oidcSecretDraft.trim();
    if (!trimmed) return; // blank = no change (use "Clear" to remove)
    await runUpdate({ oidcClientSecret: trimmed });
    setOidcSecretDraft('');
  };

  const clearOidcSecret = async () => {
    await runUpdate({ oidcClientSecret: '' });
    setOidcSecretDraft('');
  };

  const commitTutorialUrl = () => {
    const trimmed = tutorialUrlDraft.trim();
    if (trimmed === settings.tutorialVideoUrl) return;
    runUpdate({ tutorialVideoUrl: trimmed });
  };

  const commitTutorialMainText = () => {
    const trimmed = tutorialMainTextDraft.trim();
    if (trimmed === '' || trimmed === settings.tutorialMainLinkText) {
      setTutorialMainTextDraft(settings.tutorialMainLinkText);
      return;
    }
    runUpdate({ tutorialMainLinkText: trimmed });
  };

  const commitTutorialMenuText = () => {
    const trimmed = tutorialMenuTextDraft.trim();
    if (trimmed === '' || trimmed === settings.tutorialMenuLinkText) {
      setTutorialMenuTextDraft(settings.tutorialMenuLinkText);
      return;
    }
    runUpdate({ tutorialMenuLinkText: trimmed });
  };

  // Analytics cache controls
  const [analyticsRefreshing, setAnalyticsRefreshing] = useState(false);
  const [analyticsMessage, setAnalyticsMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const refreshAnalyticsCache = async () => {
    setAnalyticsRefreshing(true);
    setAnalyticsMessage(null);
    try {
      const apiBase = '/api';
      const res = await fetch(`${apiBase}/admin-analytics.php?refresh=1`, { credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      const json = await res.json();
      setAnalyticsMessage({
        kind: 'ok',
        text: `Cache rebuilt. ${(json?.totals?.estimates ?? 0).toLocaleString()} estimates aggregated.`,
      });
    } catch (err) {
      setAnalyticsMessage({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Failed to refresh cache',
      });
    } finally {
      setAnalyticsRefreshing(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all settings to defaults for every user of the application?')) return;
    setSaving(true);
    setLastError(null);
    try {
      await resetSettings();
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-3 shadow-sm p-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="fw-bold mb-1 d-flex align-items-center gap-2">
            <Settings size={22} /> Settings
          </h2>
          <p className="text-muted mb-0">
            Application-wide preferences. Changes here affect every user of the application.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
          onClick={handleReset}
          disabled={saving || isLoading}
          title="Restore every setting to its default"
        >
          <RotateCcw size={14} /> Restore defaults
        </button>
      </div>

      {(isLoading || saving) && (
        <div className="d-flex align-items-center gap-2 text-muted small mb-3">
          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
          {isLoading ? 'Loading settings from the server…' : 'Saving…'}
        </div>
      )}

      {(lastError || error) && !isLoading && (
        <div className="alert alert-warning py-2 px-3 small mb-3">
          <strong>Couldn't sync with the server.</strong> {lastError || error}
        </div>
      )}

      {/* Tab navigation — each section below is gated on the active tab. */}
      <ul className="nav nav-pills gap-1 mb-4 flex-wrap border-bottom pb-3">
        {SETTINGS_TABS.map(t => {
          const Icon = t.icon;
          return (
            <li className="nav-item" key={t.id}>
              <button
                type="button"
                className={`nav-link d-inline-flex align-items-center gap-2 ${activeTab === t.id ? 'active' : ''}`}
                onClick={() => goToTab(t.id)}
              >
                <Icon size={16} /> {t.label}
              </button>
            </li>
          );
        })}
      </ul>

      {/* ===== Estimator & Display ===== */}
      {activeTab === 'display' && (
      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1">Results visualizer transition</h5>
        <p className="text-muted small mb-3">
          Animation used when the visualizer swaps between space variants on the
          summary and shared-estimate pages.
        </p>

        <div className="row g-2 mb-4">
          {TRANSITION_OPTIONS.map(opt => {
            const active = settings.defaultTransition === opt.id;
            return (
              <div key={opt.id} className="col-12 col-md-4">
                <label
                  className={`d-block border rounded-3 p-3 h-100 ${
                    active ? 'border-primary shadow-sm' : 'border-light-subtle'
                  }`}
                  style={{
                    cursor: saving ? 'wait' : 'pointer',
                    background: active ? 'rgba(13,110,253,0.04)' : '#fff',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <input
                      type="radio"
                      name="defaultTransition"
                      value={opt.id}
                      checked={active}
                      disabled={saving}
                      onChange={() => runUpdate({ defaultTransition: opt.id as TransitionType })}
                      className="form-check-input m-0"
                    />
                    <span className="fw-semibold">{opt.label}</span>
                  </div>
                  <div className="text-muted small">{opt.description}</div>
                </label>
              </div>
            );
          })}
        </div>

        <div className="row g-4">
          {/* Transition duration */}
          <div className="col-12 col-lg-4">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <label htmlFor="transition-duration" className="fw-semibold m-0">
                Transition duration
              </label>
              <span className="badge text-bg-light" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {(settings.transitionDurationMs / 1000).toFixed(2)}s
                <span className="text-muted ms-1">({settings.transitionDurationMs}ms)</span>
              </span>
            </div>
            <p className="text-muted small mb-2">
              How long each transition plays. Shorter feels snappier; longer feels cinematic.
            </p>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small" style={{ minWidth: 40 }}>Fast</span>
              <input
                id="transition-duration"
                type="range"
                className="form-range flex-grow-1"
                min={TRANSITION_DURATION_MIN_MS}
                max={TRANSITION_DURATION_MAX_MS}
                step={TRANSITION_DURATION_STEP_MS}
                disabled={saving}
                value={settings.transitionDurationMs}
                onChange={e =>
                  runUpdate({ transitionDurationMs: clampTransitionDuration(Number(e.target.value)) })
                }
              />
              <span className="text-muted small" style={{ minWidth: 40, textAlign: 'right' }}>Slow</span>
            </div>
          </div>

          {/* Start in Play mode */}
          <div className="col-12 col-lg-4">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <label htmlFor="auto-play-toggle" className="fw-semibold m-0">
                Start in Play mode
              </label>
              <div className="form-check form-switch m-0">
                <input
                  id="auto-play-toggle"
                  type="checkbox"
                  className="form-check-input"
                  role="switch"
                  style={{ transform: 'scale(1.3)' }}
                  checked={settings.autoPlayEnabled}
                  disabled={saving}
                  onChange={e => runUpdate({ autoPlayEnabled: e.target.checked })}
                />
              </div>
            </div>
            <p className="text-muted small mb-0">
              When enabled, the visualizer begins auto-cycling through spaces immediately.
              Otherwise it stays static until the user clicks Play.
            </p>
          </div>

          {/* Time per visualization */}
          <div className="col-12 col-lg-4">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <label htmlFor="cycle-interval" className="fw-semibold m-0">
                Time per visualization
              </label>
              <span className="badge text-bg-light" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {(settings.cycleIntervalMs / 1000).toFixed(1)}s
                <span className="text-muted ms-1">({settings.cycleIntervalMs}ms)</span>
              </span>
            </div>
            <p className="text-muted small mb-2">
              How long each bitmap visualization sits on screen before the next auto-cycle swap.
            </p>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small" style={{ minWidth: 40 }}>Short</span>
              <input
                id="cycle-interval"
                type="range"
                className="form-range flex-grow-1"
                min={CYCLE_INTERVAL_MIN_MS}
                max={CYCLE_INTERVAL_MAX_MS}
                step={CYCLE_INTERVAL_STEP_MS}
                disabled={saving}
                value={settings.cycleIntervalMs}
                onChange={e =>
                  runUpdate({ cycleIntervalMs: clampCycleInterval(Number(e.target.value)) })
                }
              />
              <span className="text-muted small" style={{ minWidth: 40, textAlign: 'right' }}>Long</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* ===== Accounts & Security ===== */}
      {activeTab === 'accounts' && (
      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <MessageSquare size={18} /> Comments &amp; Spam Protection
        </h5>
        <p className="text-muted small mb-3">
          Estimate comments use Cloudflare Turnstile for bot protection. Configure
          keys in your environment and server config to enable verification.
        </p>

        <div className="row g-3">
          <div className="col-12 col-md-6">
            <div className="border rounded-3 p-3 h-100">
              <div className="d-flex align-items-center gap-2 mb-2">
                <Shield
                  size={16}
                  className={
                    !TURNSTILE_CONFIG.enabled
                      ? 'text-warning'
                      : settings.turnstileEnabled
                        ? 'text-success'
                        : 'text-secondary'
                  }
                />
                <span className="fw-semibold">Turnstile Status</span>
                <span
                  className={`badge ${
                    !TURNSTILE_CONFIG.enabled
                      ? 'text-bg-warning'
                      : settings.turnstileEnabled
                        ? 'text-bg-success'
                        : 'text-bg-secondary'
                  }`}
                >
                  {!TURNSTILE_CONFIG.enabled
                    ? 'Not Configured'
                    : settings.turnstileEnabled
                      ? 'Active'
                      : 'Disabled'}
                </span>
              </div>
              {TURNSTILE_CONFIG.enabled ? (
                <>
                  <p className="text-muted small mb-2">
                    Turnstile verification is active. Bots will be blocked from submitting
                    comments and guest estimates.
                  </p>
                  <div className="d-flex align-items-center justify-content-between border-top pt-2 mt-2">
                    <label htmlFor="turnstile-toggle" className="fw-semibold small m-0">
                      Enforce Turnstile
                    </label>
                    <div className="form-check form-switch m-0">
                      <input
                        id="turnstile-toggle"
                        type="checkbox"
                        className="form-check-input"
                        role="switch"
                        style={{ transform: 'scale(1.3)' }}
                        checked={settings.turnstileEnabled}
                        disabled={saving}
                        onChange={e => runUpdate({ turnstileEnabled: e.target.checked })}
                      />
                    </div>
                  </div>
                  <p className="text-muted small mb-0 mt-2">
                    Turn off to bypass verification on both client and server — useful
                    when testing on a local non-FQDN host where Turnstile can&rsquo;t load.
                    Leave on for production.
                  </p>
                </>
              ) : (
                <div className="text-muted small">
                  <p className="mb-2">To enable Turnstile bot protection:</p>
                  <ol className="mb-0 ps-3">
                    <li className="mb-1">
                      Get your keys from the{' '}
                      <a href="https://dash.cloudflare.com/turnstile" target="_blank" rel="noopener noreferrer">
                        Cloudflare Turnstile dashboard
                      </a>
                    </li>
                    <li className="mb-1">
                      Set <code>REACT_APP_TURNSTILE_SITE_KEY</code> in your <code>.env</code> file and rebuild
                    </li>
                    <li>
                      Set <code>TURNSTILE_SECRET_KEY</code> in <code>api/config.php</code> on the server
                    </li>
                  </ol>
                </div>
              )}
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="border rounded-3 p-3 h-100">
              <div className="d-flex align-items-center gap-2 mb-2">
                <MessageSquare size={16} className="text-primary" />
                <span className="fw-semibold">Guest Comments</span>
                <span className="badge text-bg-info">Enabled</span>
              </div>
              <p className="text-muted small mb-0">
                Visitors can post comments on shared estimates without an account.
                Comments are protected by Turnstile verification (when configured), a honeypot
                field, HTML sanitisation, and rate limiting. Guest names default to
                &ldquo;Guest&rdquo; when left blank.
              </p>
            </div>
          </div>
        </div>
      </div>

      )}

      {/* ===== General ===== */}
      {activeTab === 'general' && (
      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <Clock size={18} /> Timezone
        </h5>
        <p className="text-muted small mb-3">
          The timezone the application operates in. Estimate and comment timestamps
          are recorded and displayed in this zone across the admin (the estimate
          list, analytics hour/day breakdowns, and comment times). Defaults to{' '}
          <strong>America/Toronto</strong>.
        </p>

        <div className="mb-1" style={{ maxWidth: 440 }}>
          <label htmlFor="app-timezone" className="fw-semibold small d-block mb-1">
            Application timezone
          </label>
          <select
            id="app-timezone"
            className="form-select"
            value={settings.timezone}
            disabled={saving}
            onChange={e => runUpdate({ timezone: e.target.value })}
          >
            {(TIMEZONE_OPTIONS.includes(settings.timezone)
              ? TIMEZONE_OPTIONS
              : [settings.timezone, ...TIMEZONE_OPTIONS]
            ).map(tz => (
              <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <div className="form-text">
            {(() => {
              try {
                const nowStr = new Intl.DateTimeFormat('en-US', {
                  timeZone: settings.timezone, dateStyle: 'medium', timeStyle: 'short',
                }).format(new Date());
                return <>Current time there: <strong>{nowStr}</strong></>;
              } catch {
                return 'Invalid timezone selected.';
              }
            })()}
          </div>
        </div>
        <p className="text-muted small mb-0 mt-2">
          <strong>Note:</strong> this should match your database server&rsquo;s clock.
          Existing timestamps are not shifted &mdash; this controls how times are
          interpreted and labelled going forward.
        </p>
      </div>

      )}

      {activeTab === 'general' && (<>
      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <SlidersHorizontal size={18} /> 3D Designer
        </h5>
        <p className="text-muted small mb-3">
          Rules the 3D designer applies when windows and doors are placed on a wall.
        </p>
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <label htmlFor="opening-safe-margin" className="fw-semibold small d-block mb-1">Opening safe margin (inches)</label>
            <input
              id="opening-safe-margin"
              type="number"
              min={0}
              max={24}
              step={0.5}
              className="form-control"
              value={settings.openingSafeMarginIn}
              disabled={saving}
              onChange={e => runUpdate({ openingSafeMarginIn: Number(e.target.value) })}
            />
            <div className="form-text">
              The closest a window or door may sit to the edge of its wall. Doors and garage doors stand on the
              floor, so this does not apply to their bottom edge. 0 allows openings right up to the edge.
            </div>
          </div>
        </div>
        <div className="row g-3 mt-1">
          <div className="col-12 col-lg-6">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <label htmlFor="advanced-designer-toggle" className="fw-semibold m-0">
                Advanced mode for everyone
              </label>
              <div className="form-check form-switch m-0">
                <input
                  id="advanced-designer-toggle"
                  type="checkbox"
                  className="form-check-input"
                  role="switch"
                  style={{ transform: 'scale(1.3)' }}
                  checked={settings.advancedDesignerEnabled}
                  disabled={saving}
                  onChange={e => runUpdate({ advancedDesignerEnabled: e.target.checked })}
                />
              </div>
            </div>
            <p className="text-muted small mb-0">
              Shows the Advanced card (floor planner + 3D designer) under the three project types on
              step 1, and opens <code>/design</code> to every visitor. Off, beta testers can still turn
              it on for their own browser with <kbd>Ctrl</kbd>+<kbd>Alt</kbd>+<kbd>Shift</kbd>+<kbd>A</kbd>
              (press again to turn it off), or by opening <code>/design?advanced=1</code>.
            </p>
          </div>
          <div className="col-12 col-lg-6">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <label htmlFor="advanced-multiroom-toggle" className="fw-semibold m-0">
                Several rooms per blueprint
              </label>
              <div className="form-check form-switch m-0">
                <input
                  id="advanced-multiroom-toggle"
                  type="checkbox"
                  className="form-check-input"
                  role="switch"
                  style={{ transform: 'scale(1.3)' }}
                  checked={settings.advancedDesignerMultiRoom}
                  disabled={saving}
                  onChange={e => runUpdate({ advancedDesignerMultiRoom: e.target.checked })}
                />
              </div>
            </div>
            <p className="text-muted small mb-0">
              Off (recommended for now): each blueprint holds one room, created from the room form.
              On: the Draw tool returns and rooms can be joined along shared walls, as in the sandbox.
              Turning it off again keeps only the first room of any blueprint that has more.
            </p>
          </div>
          <div className="col-12 col-lg-6">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <label htmlFor="render-queue-toggle" className="fw-semibold m-0">
                Photoreal renders on save
              </label>
              <div className="form-check form-switch m-0">
                <input
                  id="render-queue-toggle"
                  type="checkbox"
                  className="form-check-input"
                  role="switch"
                  style={{ transform: 'scale(1.3)' }}
                  checked={settings.renderQueueEnabled}
                  disabled={saving}
                  onChange={e => runUpdate({ renderQueueEnabled: e.target.checked })}
                />
              </div>
            </div>
            <p className="text-muted small mb-0">
              When an estimate made in Advanced mode is saved, queue a Blender render of the room
              (the user's view, four corners and a 360) for the saved estimate page. Needs the render
              worker running on a machine with a GPU and <code>RENDER_WORKER_KEY</code> set in{' '}
              <code>api/config.php</code>. Off: the page shows the quick preview only.
            </p>
            <div className="row g-2 mt-2">
              <div className="col-6">
                <label htmlFor="render-active-limit" className="form-label small fw-semibold mb-1">Unfinished renders per person</label>
                <input id="render-active-limit" type="number" min={0} max={100} className="form-control" value={settings.renderActiveLimit} disabled={saving} onChange={e => runUpdate({ renderActiveLimit: Number(e.target.value) })} />
              </div>
              <div className="col-6">
                <label htmlFor="render-daily-limit" className="form-label small fw-semibold mb-1">Renders per person per day</label>
                <input id="render-daily-limit" type="number" min={0} max={1000} className="form-control" value={settings.renderDailyLimit} disabled={saving} onChange={e => runUpdate({ renderDailyLimit: Number(e.target.value) })} />
              </div>
            </div>
            <p className="text-muted small mb-0 mt-1">
              0 = no limit. A newer render stops the person's oldest unfinished one; re-saving an unchanged
              room queues nothing. Guests are counted by email and by address. Admins, retailers' users and
              anyone holding the <code>render-unlimited</code> role in Keycloak are never limited.
            </p>
          </div>
        </div>
      </div>

      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <SlidersHorizontal size={18} /> Space types
        </h5>
        <p className="text-muted small mb-2">
          Where each kind of space is offered: in each of the wizard's modes, and in Advanced mode. Off hides it
          from the choice; estimates already made with it are unchanged. The spaces themselves are the app's list
          (<code>src/data/presets.ts</code>).
        </p>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Space</th>
                {SPACE_USES.map(u => <th key={u.key} className="text-center">{u.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {SPACE_TYPES.map(t => (
                <tr key={t.id}>
                  <td>{t.label}</td>
                  {SPACE_USES.map(u => (
                    <td key={u.key} className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        aria-label={`${t.label}: ${u.label}`}
                        checked={spaceAllowed(settings.spaceTypeAccess, t.id, u.key)}
                        disabled={saving}
                        onChange={e => runUpdate({ spaceTypeAccess: { ...settings.spaceTypeAccess, [t.id]: { ...(settings.spaceTypeAccess[t.id] ?? {}), [u.key]: e.target.checked } } })}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>)}

      {activeTab === 'general' && (
      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <BarChart3 size={18} /> Analytics Cache
        </h5>
        <p className="text-muted small mb-3">
          The Admin Analytics page aggregates every completed estimate on demand.
          Results are cached on the server for <strong>5 minutes</strong> so back-to-back
          page loads don&rsquo;t re-scan the table. The Refresh button on the Analytics
          page (and the button below) bypasses the cache and rebuilds it now. No
          cron job required &mdash; aggregation runs at request time and the table is
          small enough to make this cheap.
        </p>

        <div className="d-flex flex-wrap align-items-center gap-2">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
            onClick={refreshAnalyticsCache}
            disabled={analyticsRefreshing}
          >
            <RefreshCw size={14} style={analyticsRefreshing ? { animation: 'spin 1s linear infinite' } : undefined} />
            Rebuild analytics cache now
          </button>
          {analyticsMessage && (
            <span className={`small ${analyticsMessage.kind === 'ok' ? 'text-success' : 'text-danger'}`}>
              {analyticsMessage.text}
            </span>
          )}
        </div>
      </div>

      )}

      {/* ===== Integrations ===== */}
      {activeTab === 'integrations' && (
      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <Send size={18} /> Pardot Integration
        </h5>
        <p className="text-muted small mb-3">
          When a guest submits the Detailed Materials Breakdown form, the server
          POSTs the lead to the configured Pardot form handler. Field names match
          the External Field Name column on the Pardot form handler mapping.
        </p>

        <div className="mb-3">
          <label htmlFor="pardot-url" className="fw-semibold small d-block mb-1">
            Form handler URL
          </label>
          <input
            id="pardot-url"
            type="url"
            className="form-control"
            placeholder="https://go.pardot.com/l/..."
            value={pardotUrlDraft}
            disabled={saving}
            onChange={e => setPardotUrlDraft(e.target.value)}
            onBlur={commitPardotUrl}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.currentTarget as HTMLInputElement).blur();
              }
            }}
          />
          <div className="form-text">
            Leave empty to disable submissions regardless of the toggle below.
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="pardot-friendly-name" className="fw-semibold small d-block mb-1">
            Friendly name
          </label>
          <input
            id="pardot-friendly-name"
            type="text"
            className="form-control"
            placeholder="Cost & Materials Estimator"
            value={pardotFriendlyNameDraft}
            disabled={saving}
            onChange={e => setPardotFriendlyNameDraft(e.target.value)}
            onBlur={commitPardotFriendlyName}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.currentTarget as HTMLInputElement).blur();
              }
            }}
          />
          <div className="form-text">
            Sent as the <code>FriendlyName</code> field so Pardot can attribute
            leads to this form. Cannot be empty.
          </div>
        </div>

        <div className="row g-3">
          <div className="col-12 col-md-6">
            <div className="border rounded-3 p-3 h-100">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                  <Send
                    size={16}
                    className={settings.pardotEnabled ? 'text-success' : 'text-secondary'}
                  />
                  <span className="fw-semibold">Pardot submissions</span>
                  <span
                    className={`badge ${
                      settings.pardotEnabled
                        ? settings.pardotFormHandlerUrl
                          ? 'text-bg-success'
                          : 'text-bg-warning'
                        : 'text-bg-secondary'
                    }`}
                  >
                    {settings.pardotEnabled
                      ? settings.pardotFormHandlerUrl
                        ? 'Active'
                        : 'No URL'
                      : 'Disabled'}
                  </span>
                </div>
                <div className="form-check form-switch m-0">
                  <input
                    id="pardot-enabled-toggle"
                    type="checkbox"
                    className="form-check-input"
                    role="switch"
                    style={{ transform: 'scale(1.3)' }}
                    checked={settings.pardotEnabled}
                    disabled={saving}
                    onChange={e => runUpdate({ pardotEnabled: e.target.checked })}
                  />
                </div>
              </div>
              <p className="text-muted small mb-0">
                Turn on to POST submitted leads to Pardot. Submissions only run
                when a URL is configured above and Turnstile verification
                (when enabled) passes on the server.
              </p>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="border rounded-3 p-3 h-100">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                  <Bug
                    size={16}
                    className={settings.pardotDebug ? 'text-warning' : 'text-secondary'}
                  />
                  <span className="fw-semibold">Debug payload</span>
                  <span
                    className={`badge ${settings.pardotDebug ? 'text-bg-warning' : 'text-bg-secondary'}`}
                  >
                    {settings.pardotDebug ? 'Logging' : 'Off'}
                  </span>
                </div>
                <div className="form-check form-switch m-0">
                  <input
                    id="pardot-debug-toggle"
                    type="checkbox"
                    className="form-check-input"
                    role="switch"
                    style={{ transform: 'scale(1.3)' }}
                    checked={settings.pardotDebug}
                    disabled={saving}
                    onChange={e => runUpdate({ pardotDebug: e.target.checked })}
                  />
                </div>
              </div>
              <p className="text-muted small mb-0">
                When on, the exact payload the server built for Pardot is returned
                in the save-estimate API response and logged to the browser
                console as <code>[Pardot debug payload]</code> — whether or not
                submissions are enabled. Turn off in production.
              </p>
            </div>
          </div>
        </div>
      </div>

      )}

      {activeTab === 'integrations' && (
      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <MessageSquare size={18} /> Gleap Chat Widget
        </h5>
        <p className="text-muted small mb-3">
          Shows the Gleap chat launcher to visitors. The estimator stores nothing:
          chats go to the Gleap project, and the trusscore.com Gleap webhook
          receiver delivers each chat lead to Pardot once, tagged by the page host
          (estimator chats as <code>Estimator Chat</code>). Use the same SDK key as
          trusscore.com so chats share one inbox.
        </p>

        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6">
            <div className="border rounded-3 p-3 h-100">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                  <MessageSquare
                    size={16}
                    className={settings.gleapEnabled ? 'text-success' : 'text-secondary'}
                  />
                  <span className="fw-semibold">Chat widget</span>
                  <span
                    className={`badge ${
                      settings.gleapEnabled
                        ? settings.gleapSdkKey
                          ? 'text-bg-success'
                          : 'text-bg-warning'
                        : 'text-bg-secondary'
                    }`}
                  >
                    {settings.gleapEnabled
                      ? settings.gleapSdkKey
                        ? 'Active'
                        : 'No key'
                      : 'Disabled'}
                  </span>
                </div>
                <div className="form-check form-switch m-0">
                  <input
                    id="gleap-enabled-toggle"
                    type="checkbox"
                    className="form-check-input"
                    role="switch"
                    style={{ transform: 'scale(1.3)' }}
                    checked={settings.gleapEnabled}
                    disabled={saving}
                    onChange={e => runUpdate({ gleapEnabled: e.target.checked })}
                  />
                </div>
              </div>
              <p className="text-muted small mb-0">
                Master switch. The Gleap script only loads when this is on and an
                SDK key is set below.
              </p>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <label htmlFor="gleap-sdk-key" className="fw-semibold small d-block mb-1">
              SDK key
            </label>
            <input
              id="gleap-sdk-key"
              type="text"
              className="form-control font-monospace"
              placeholder="Gleap project SDK key"
              autoComplete="off"
              spellCheck={false}
              value={gleapKeyDraft}
              disabled={saving}
              onChange={e => setGleapKeyDraft(e.target.value)}
              onBlur={commitGleapKey}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.currentTarget as HTMLInputElement).blur();
                }
              }}
            />
            <div className="form-text">
              Gleap → Project settings → Installation. A public browser key, the
              same value as trusscore.com's <code>gleap.sdk_key</code>.
            </div>
          </div>
        </div>

        <div className="row g-3 mb-3">
          {([
            {
              id: 'gleap-partner-toggle',
              key: 'gleapPartnerPortals',
              label: 'Partner portals',
              help: 'Also show the widget on branded retailer portals (<slug>.estimator.trusscore.com).',
            },
            {
              id: 'gleap-language-toggle',
              key: 'gleapSyncLanguage',
              label: 'Match app language',
              help: "Keep the widget's language in step with the language the visitor picked in the app.",
            },
            {
              id: 'gleap-clarity-toggle',
              key: 'gleapClarityLink',
              label: 'Attach Clarity session',
              help: 'When the widget opens, attach the Microsoft Clarity recording URL to the ticket. Does nothing if Clarity is not loaded.',
            },
          ] as const).map(opt => (
            <div className="col-12 col-md-4" key={opt.id}>
              <div className="border rounded-3 p-3 h-100">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-semibold">{opt.label}</span>
                  <div className="form-check form-switch m-0">
                    <input
                      id={opt.id}
                      type="checkbox"
                      className="form-check-input"
                      role="switch"
                      style={{ transform: 'scale(1.3)' }}
                      checked={settings[opt.key]}
                      disabled={saving}
                      onChange={e => runUpdate({ [opt.key]: e.target.checked })}
                    />
                  </div>
                </div>
                <p className="text-muted small mb-0">{opt.help}</p>
              </div>
            </div>
          ))}
        </div>

        <div>
          <label htmlFor="gleap-exclude-paths" className="fw-semibold small d-block mb-1">
            Hide on these paths
          </label>
          <textarea
            id="gleap-exclude-paths"
            className="form-control font-monospace"
            rows={3}
            placeholder={'/mtl\n/estimate'}
            value={gleapExcludeDraft}
            disabled={saving}
            onChange={e => setGleapExcludeDraft(e.target.value)}
            onBlur={commitGleapExclude}
          />
          <div className="form-text">
            One path prefix per line, each starting with <code>/</code>. The widget
            is always hidden on <code>/admin</code>.
          </div>
        </div>
      </div>
      )}

      {activeTab === 'integrations' && (
      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <MapPin size={18} /> Address Input &amp; Geocoding
        </h5>
        <p className="text-muted small mb-3">
          How the guest-estimate form collects location, and which geocoding vendor answers
          type-ahead searches — for the address form <em>and</em> the location-map search box.
          <strong> Classic</strong> uses the admin-managed Country / Region selects (see{' '}
          <code>Locations</code>). <strong>Autocomplete</strong> swaps in a full-address typeahead.
          Every vendor call is proxied <strong>server-side</strong>: keys never reach the browser,
          answers are cached, and the same registry serves both features.
        </p>

        <div className="row g-3 mb-3">
          <div className="col-12 col-md-4">
            <label htmlFor="addr-mode" className="fw-semibold small d-block mb-1">Input mode</label>
            <select
              id="addr-mode"
              className="form-select"
              value={settings.addressInputMode}
              disabled={saving}
              onChange={e => runUpdate({ addressInputMode: e.target.value as 'classic' | 'autocomplete' })}
            >
              <option value="classic">Classic — Country / Region selects</option>
              <option value="autocomplete">Autocomplete — full-address typeahead</option>
            </select>
          </div>
          <div className="col-12 col-md-4">
            <label htmlFor="addr-provider" className="fw-semibold small d-block mb-1">Address provider</label>
            <select
              id="addr-provider"
              className="form-select"
              value={settings.addressProvider}
              disabled={saving}
              onChange={e => runUpdate({ addressProvider: e.target.value })}
            >
              {(geoProviders ?? GEO_PROVIDER_FALLBACK).map(p => (
                <option key={p.name} value={p.name}>
                  {p.label}{p.keyless ? ' — keyless' : (p.configured ? ' — key set' : ' — needs key')}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-4">
            <label htmlFor="map-provider" className="fw-semibold small d-block mb-1">Map search provider</label>
            <select
              id="map-provider"
              className="form-select"
              value={settings.mapSearchProvider}
              disabled={saving}
              onChange={e => runUpdate({ mapSearchProvider: e.target.value })}
            >
              <option value="">Same as address provider</option>
              {(geoProviders ?? GEO_PROVIDER_FALLBACK).map(p => (
                <option key={p.name} value={p.name}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-check form-switch mb-3">
          <input
            id="addr-restrict"
            type="checkbox"
            role="switch"
            className="form-check-input"
            checked={settings.addressRestrictToCountries}
            disabled={saving}
            onChange={e => runUpdate({ addressRestrictToCountries: e.target.checked })}
          />
          <label htmlFor="addr-restrict" className="form-check-label">
            Limit suggestions to my configured countries
            <span className="form-text d-block mt-0">
              Restricts the typeahead to the active countries under{' '}
              <code>Locations</code> (e.g. Canada, United States, Cayman Islands) —
              filters out the rest. Applied server-side via the provider&apos;s country filter.
            </span>
          </label>
        </div>

        <div className="form-check form-switch mb-3">
          <input
            id="addr-nudge"
            type="checkbox"
            role="switch"
            className="form-check-input"
            checked={settings.addressNudgeIncomplete}
            disabled={saving}
            onChange={e => runUpdate({ addressNudgeIncomplete: e.target.checked })}
          />
          <label htmlFor="addr-nudge" className="form-check-label">
            Nudge on incomplete addresses
            <span className="form-text d-block mt-0">
              If someone picks a street-level result with no building number
              (e.g. &quot;Ontario Street&quot;), show a gentle hint to add it.
              <strong> Never blocks submission</strong> — just improves data quality.
            </span>
          </label>
        </div>

        {/* One card per provider in use (address + map search, de-duplicated). */}
        {Array.from(new Set([settings.addressProvider, settings.mapSearchProvider || settings.addressProvider])).map(name => {
          const meta = (geoProviders ?? GEO_PROVIDER_FALLBACK).find(p => p.name === name) ?? { name, label: name, keyless: false, freeTier: '', attribution: false as const, supportsDetails: false, needsSession: false, configured: false };
          const keySet = !!settings.geoKeysSet[name];
          const test = geoTest[name];
          return (
            <div key={name} className="border rounded-3 p-3 mb-3 bg-light-subtle">
              <div className="d-flex align-items-center gap-2 mb-1">
                <strong>{meta.label}</strong>
                {meta.keyless
                  ? <span className="badge text-bg-info">Keyless</span>
                  : <span className={`badge ${keySet ? 'text-bg-success' : 'text-bg-secondary'}`}>{keySet ? 'Key configured' : 'No key'}</span>}
                {settings.addressProvider === name && <span className="badge text-bg-light border">address form</span>}
                {(settings.mapSearchProvider || settings.addressProvider) === name && <span className="badge text-bg-light border">map search</span>}
              </div>
              {meta.freeTier && <div className="small text-muted mb-2">{meta.freeTier}</div>}
              {meta.refused && <div className="alert alert-warning py-2 small mb-2">{meta.refused}</div>}
              {meta.keyHint && <div className="small mb-2">{meta.keyHint}</div>}
              {meta.attribution && (
                <div className="small mb-2">
                  This vendor requires a visible attribution link — the estimator renders
                  &quot;{meta.attribution}&quot; under the search field automatically.
                </div>
              )}

              {!meta.keyless && (
                <div className="mb-2">
                  <div className="input-group">
                    <input
                      type="password"
                      className="form-control"
                      autoComplete="off"
                      aria-label={`${meta.label} API key`}
                      placeholder={keySet ? 'Enter a new key to replace the stored one' : 'Paste the API key'}
                      value={geoKeyDraft[name] ?? ''}
                      disabled={saving}
                      onChange={e => setGeoKeyDraft(d => ({ ...d, [name]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitGeoKey(name); } }}
                    />
                    <button type="button" className="btn btn-primary" disabled={saving || !(geoKeyDraft[name] ?? '').trim()} onClick={() => commitGeoKey(name)}>
                      Save key
                    </button>
                    {keySet && (
                      <button type="button" className="btn btn-outline-danger" disabled={saving} onClick={() => clearGeoKey(name)}>
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="form-text">
                    Write-only — never displayed or sent to the browser. Restrict the key by
                    <strong> server IP</strong> (or leave unrestricted); an HTTP-referrer restriction blocks server calls.
                  </div>
                </div>
              )}

              {name === 'photon' && (
                <div className="mb-2">
                  <label className="fw-semibold small d-block mb-1" htmlFor="geo-photon-endpoint">Photon endpoint</label>
                  <input
                    id="geo-photon-endpoint"
                    type="url"
                    className="form-control"
                    value={photonEndpointDraft}
                    disabled={saving}
                    onChange={e => setPhotonEndpointDraft(e.target.value)}
                    onBlur={() => { const v = photonEndpointDraft.trim(); if (v !== settings.geoPhotonEndpoint) runUpdate({ geoPhotonEndpoint: v }); }}
                  />
                  <div className="form-text">Blank restores the public komoot instance. Point at a self-hosted Photon for volume or an SLA.</div>
                </div>
              )}
              {name === 'nominatim' && (
                <div className="row g-2 mb-2">
                  <div className="col-12 col-md-7">
                    <label className="fw-semibold small d-block mb-1" htmlFor="geo-nominatim-endpoint">Nominatim endpoint</label>
                    <input
                      id="geo-nominatim-endpoint"
                      type="url"
                      className="form-control"
                      value={nominatimEndpointDraft}
                      disabled={saving}
                      onChange={e => setNominatimEndpointDraft(e.target.value)}
                      onBlur={() => { const v = nominatimEndpointDraft.trim(); if (v !== settings.geoNominatimEndpoint) runUpdate({ geoNominatimEndpoint: v }); }}
                    />
                  </div>
                  <div className="col-12 col-md-5">
                    <label className="fw-semibold small d-block mb-1" htmlFor="geo-nominatim-email">Contact email (usage policy)</label>
                    <input
                      id="geo-nominatim-email"
                      type="email"
                      className="form-control"
                      value={nominatimEmailDraft}
                      disabled={saving}
                      onChange={e => setNominatimEmailDraft(e.target.value)}
                      onBlur={() => { const v = nominatimEmailDraft.trim(); if (v !== settings.geoNominatimEmail) runUpdate({ geoNominatimEmail: v }); }}
                    />
                  </div>
                </div>
              )}

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={saving || test?.status === 'busy'} onClick={() => testGeoProvider(name)}>
                  {test?.status === 'busy' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Test
                </button>
                {test && (
                  <span className={`small ${test.status === 'ok' ? 'text-success' : test.status === 'err' ? 'text-danger' : 'text-muted'}`}>{test.text}</span>
                )}
              </div>
            </div>
          );
        })}

        {settings.addressInputMode === 'autocomplete' && !settings.addressApiKeySet && (
          <div className="alert alert-warning py-2 small">
            Autocomplete is on but the address provider has no key — the typeahead returns
            no suggestions until one is saved (fields still accept manual entry).
          </div>
        )}

        <details className="mt-2">
          <summary className="small fw-semibold">Advanced — bias, cache, rate limit, GeoIP</summary>
          <div className="row g-3 mt-1">
            <div className="col-12 col-md-6">
              <div className="form-check form-switch">
                <input id="geo-bias-ip" type="checkbox" role="switch" className="form-check-input" checked={settings.geoBiasIp} disabled={saving} onChange={e => runUpdate({ geoBiasIp: e.target.checked })} />
                <label htmlFor="geo-bias-ip" className="form-check-label">
                  Bias searches toward the visitor&apos;s IP location
                  <span className="form-text d-block mt-0">When a search carries no explicit point or country, use GeoIP to rank nearby results first.</span>
                </label>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <label htmlFor="geo-ip-provider" className="fw-semibold small d-block mb-1">GeoIP provider</label>
              <select id="geo-ip-provider" className="form-select" value={settings.geoIpProvider} disabled={saving} onChange={e => runUpdate({ geoIpProvider: e.target.value as 'ip-api' | 'none' })}>
                <option value="ip-api">ip-api.com (keyless, cached 24 h)</option>
                <option value="none">Off</option>
              </select>
            </div>
            <div className="col-12 col-md-6">
              <label htmlFor="geo-cache" className="fw-semibold small d-block mb-1">Answer cache (seconds)</label>
              <input id="geo-cache" type="number" min={0} max={604800} className="form-control" value={settings.geoCacheSeconds} disabled={saving} onChange={e => runUpdate({ geoCacheSeconds: Number(e.target.value) })} />
              <div className="form-text">Identical searches within this window never hit the vendor. 0 disables.</div>
            </div>
            <div className="col-12 col-md-6">
              <label htmlFor="geo-rate" className="fw-semibold small d-block mb-1">Rate limit (requests / minute / IP)</label>
              <input id="geo-rate" type="number" min={0} max={1000} className="form-control" value={settings.geoRateLimitPerMin} disabled={saving} onChange={e => runUpdate({ geoRateLimitPerMin: Number(e.target.value) })} />
              <div className="form-text">Protects paid keys from a runaway client. 0 disables.</div>
            </div>
          </div>
        </details>
      </div>

      )}

      {activeTab === 'integrations' && (
      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <MapPin size={18} /> Location Maps
        </h5>
        <p className="text-muted small mb-3">
          Retailer and installer maps on estimate results read their locations from{' '}
          <strong>trusscore.com</strong> (the retailermaps estimator endpoint) through this server —
          never from the browser. Configure the endpoint and its shared key here; design the maps
          themselves under <a href="/admin/maps">Location Maps</a>.
        </p>

        <div className="row g-3 mb-3">
          <div className="col-12 col-lg-7">
            <label htmlFor="maps-upstream" className="fw-semibold small d-block mb-1">Upstream endpoint URL</label>
            <input
              id="maps-upstream"
              type="url"
              className="form-control"
              placeholder="https://trusscore.com/assets/components/retailermaps/estimator.php"
              value={mapsUpstreamDraft}
              disabled={saving}
              onChange={e => setMapsUpstreamDraft(e.target.value)}
              onBlur={() => { const v = mapsUpstreamDraft.trim(); if (v !== settings.mapsUpstreamBaseUrl) runUpdate({ mapsUpstreamBaseUrl: v }); }}
            />
            <div className="form-text">The retailermaps package's <code>estimator.php</code> on the site that holds the retailer database.</div>
          </div>
          <div className="col-12 col-lg-5">
            <label htmlFor="maps-key" className="fw-semibold small d-block mb-1">
              Shared key{' '}
              <span className={`badge ${settings.mapsUpstreamKeySet ? 'text-bg-success' : 'text-bg-secondary'}`}>
                {settings.mapsUpstreamKeySet ? 'Configured' : 'Not set'}
              </span>
            </label>
            <div className="input-group">
              <input
                id="maps-key"
                type="password"
                className="form-control"
                autoComplete="off"
                placeholder={settings.mapsUpstreamKeySet ? 'Enter a new key to replace the stored one' : 'Paste retailermaps.estimator_key'}
                value={mapsKeyDraft}
                disabled={saving}
                onChange={e => setMapsKeyDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void commitMapsKey(); } }}
              />
              <button type="button" className="btn btn-primary" disabled={saving || !mapsKeyDraft.trim()} onClick={() => void commitMapsKey()}>Save key</button>
              {settings.mapsUpstreamKeySet && (
                <button type="button" className="btn btn-outline-danger" disabled={saving} onClick={() => void runUpdate({ mapsUpstreamKey: '' })}>Clear</button>
              )}
            </div>
            <div className="form-text">Write-only. Must equal <code>retailermaps.estimator_key</code> on trusscore.com (then Clear Cache there).</div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
          <button type="button" className="btn btn-sm btn-outline-secondary" disabled={saving || mapsTest?.status === 'busy'} onClick={() => void testMapsUpstream()}>
            {mapsTest?.status === 'busy' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Test connection
          </button>
          {mapsTest && <span className={`small ${mapsTest.status === 'ok' ? 'text-success' : mapsTest.status === 'err' ? 'text-danger' : 'text-muted'}`}>{mapsTest.text}</span>}
        </div>

        <div className="row g-3">
          <div className="col-12 col-lg-7">
            <label htmlFor="maps-style" className="fw-semibold small d-block mb-1">Default tile style URL</label>
            <input
              id="maps-style"
              type="url"
              className="form-control"
              value={mapsStyleDraft}
              disabled={saving}
              onChange={e => setMapsStyleDraft(e.target.value)}
              onBlur={() => { const v = mapsStyleDraft.trim(); if (v !== settings.mapsDefaultTileStyleUrl) runUpdate({ mapsDefaultTileStyleUrl: v }); }}
            />
            <div className="form-text">
              A MapLibre style JSON. Free: <code>https://tiles.openfreemap.org/styles/liberty</code> (or bright, positron, fiord).
              Paid vector providers (MapTiler, Stadia, Mapbox) work by URL — put <code>{'{key}'}</code> where the key goes, or leave it out and the key below is appended.
            </div>
          </div>
          <div className="col-12 col-lg-5">
            <label htmlFor="maps-tile-key" className="fw-semibold small d-block mb-1">Tile provider key (public)</label>
            <input
              id="maps-tile-key"
              type="text"
              className="form-control"
              value={mapsTileKeyDraft}
              disabled={saving}
              onChange={e => setMapsTileKeyDraft(e.target.value)}
              onBlur={() => { const v = mapsTileKeyDraft.trim(); if (v !== settings.mapsTileApiKey) runUpdate({ mapsTileApiKey: v }); }}
            />
            <div className="form-text">
              <strong>Sent to the browser</strong> (tiles are fetched client-side), so this must be an
              <strong> HTTP-referrer-restricted</strong> key — never the server-side geocoding key. Blank for OpenFreeMap.
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <label htmlFor="maps-units" className="fw-semibold small d-block mb-1">Default units</label>
            <select id="maps-units" className="form-select" value={settings.mapsDefaultUnits} disabled={saving} onChange={e => runUpdate({ mapsDefaultUnits: e.target.value as 'km' | 'miles' })}>
              <option value="km">Kilometres</option>
              <option value="miles">Miles</option>
            </select>
          </div>
          <div className="col-6 col-lg-3">
            <label htmlFor="maps-data-ttl" className="fw-semibold small d-block mb-1">Data cache (seconds)</label>
            <input id="maps-data-ttl" type="number" min={0} max={3600} className="form-control" value={settings.mapsDataCacheTtl} disabled={saving} onChange={e => runUpdate({ mapsDataCacheTtl: Number(e.target.value) })} />
            <div className="form-text">Identical map queries within this window share one trusscore.com call; stale answers are served if it is down. 0 disables.</div>
          </div>
          <div className="col-6 col-lg-3">
            <label htmlFor="maps-image-ttl" className="fw-semibold small d-block mb-1">Pin/logo cache (seconds)</label>
            <input id="maps-image-ttl" type="number" min={0} max={2592000} className="form-control" value={settings.mapsImageCacheTtl} disabled={saving} onChange={e => runUpdate({ mapsImageCacheTtl: Number(e.target.value) })} />
            <div className="form-text">Pins and logos are proxied and cached on this server. 0 = load them straight from trusscore.com.</div>
          </div>
        </div>
      </div>
      )}

      {/* ===== Accounts & Security (cont.) ===== */}
      {activeTab === 'accounts' && (
      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <LogIn size={18} /> Single Sign-On (Keycloak){' '}
          <span className={`badge ${settings.oidcConfigured ? 'text-bg-success' : 'text-bg-secondary'}`}>
            {settings.oidcConfigured ? 'Configured' : 'Not configured'}
          </span>
        </h5>
        <p className="text-muted small mb-3">
          OIDC login against Trusscore&apos;s Keycloak realm. These override the
          <code> config.php</code> fallback and are stored per-database. The client
          secret is write-only — stored server-side, never sent to the browser.
          Leave the issuer/client id blank to fall back to <code>config.php</code>.
        </p>

        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6">
            <label htmlFor="oidc-issuer" className="fw-semibold small d-block mb-1">Issuer URL</label>
            <input
              id="oidc-issuer"
              type="text"
              className="form-control"
              placeholder="https://auth.trusscore.com/realms/trusscore"
              value={oidcIssuerDraft}
              disabled={saving}
              onChange={e => setOidcIssuerDraft(e.target.value)}
              onBlur={commitOidcIssuer}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitOidcIssuer(); } }}
            />
          </div>
          <div className="col-12 col-md-6">
            <label htmlFor="oidc-client-id" className="fw-semibold small d-block mb-1">Client ID</label>
            <input
              id="oidc-client-id"
              type="text"
              className="form-control"
              placeholder="estimator-web"
              value={oidcClientIdDraft}
              disabled={saving}
              onChange={e => setOidcClientIdDraft(e.target.value)}
              onBlur={commitOidcClientId}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitOidcClientId(); } }}
            />
          </div>
        </div>

        <div className="mb-2">
          <label htmlFor="oidc-secret" className="fw-semibold small d-block mb-1">
            Client secret{' '}
            <span className={`badge ${settings.oidcClientSecretSet ? 'text-bg-success' : 'text-bg-secondary'}`}>
              {settings.oidcClientSecretSet ? 'Configured' : 'Not set'}
            </span>
          </label>
          <div className="input-group">
            <input
              id="oidc-secret"
              type="password"
              className="form-control"
              autoComplete="off"
              placeholder={settings.oidcClientSecretSet ? 'Enter a new secret to replace the stored one' : 'Paste the estimator-web client secret'}
              value={oidcSecretDraft}
              disabled={saving}
              onChange={e => setOidcSecretDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitOidcSecret(); } }}
            />
            <button type="button" className="btn btn-primary" disabled={saving || !oidcSecretDraft.trim()} onClick={commitOidcSecret}>
              Save secret
            </button>
            {settings.oidcClientSecretSet && (
              <button type="button" className="btn btn-outline-danger" disabled={saving} onClick={clearOidcSecret}>
                Clear
              </button>
            )}
          </div>
          <div className="form-text">
            Confidential server-side (BFF) flow. Every branded portal sends Keycloak to
            one canonical callback, so <code>estimator-web</code> needs exactly{' '}
            <strong>one</strong> redirect URI —{' '}
            <code>https://estimator.trusscore.com/api/auth-callback.php</code> — no
            matter how many retailer subdomains launch. (Keycloak cannot wildcard a
            host: <code>https://*.estimator.trusscore.com/*</code> matches nothing.)
            Admin/super tiers come from the <code>admin</code> / <code>super-admin</code>{' '}
            realm roles.
          </div>
        </div>

        <hr className="my-3" />

        <div className="d-flex justify-content-between align-items-start gap-3">
          <label htmlFor="accounts-enabled-toggle" className="form-check-label mb-0">
            <span className="fw-semibold">Accounts &amp; login system (master switch)</span>
            <span className="form-text d-block mt-0">
              The master on/off for the entire accounts system. <strong>Off</strong> (default):
              guest-only app with no login/sign-up UI, and admins sign in with the{' '}
              <strong>password</strong> — today&apos;s behavior. <strong>On</strong>: admin and
              end-user login switch to <strong>Keycloak</strong>, and the accounts UI appears.
              Turning it on requires OIDC to be configured (the server refuses otherwise). Keep a
              break-glass admin password in <code>config.php</code> before switching on — see the
              plan doc. Leave off until you&apos;re ready to launch accounts.
            </span>
          </label>
          <div className="form-check form-switch m-0">
            <input
              id="accounts-enabled-toggle"
              type="checkbox"
              className="form-check-input"
              role="switch"
              style={{ transform: 'scale(1.3)' }}
              checked={settings.accountsEnabled}
              disabled={saving}
              onChange={e => runUpdate({ accountsEnabled: e.target.checked })}
            />
          </div>
        </div>

        <hr className="my-3" />

        <div className="d-flex justify-content-between align-items-start gap-3">
          <label htmlFor="native-auth-toggle" className="form-check-label mb-0">
            <span className="fw-semibold">Native in-app login (modals)</span>
            <span className="form-text d-block mt-0">
              Only applies when the accounts system above is on.{' '}
              When on, the app shows its own login/sign-up UI in modals instead of
              redirecting to Keycloak&apos;s hosted pages. Password sign-in is handled
              server-side (BFF); social logins still redirect. Verification and
              password-reset codes are emailed by <strong>this app</strong>, so{' '}
              <strong>SMTP must be configured and enabled below</strong> — the toggle
              is refused otherwise, since users could not sign up or reset a password
              at all. Leave off to use the hosted pages.
            </span>
          </label>
          <div className="form-check form-switch m-0">
            <input
              id="native-auth-toggle"
              type="checkbox"
              className="form-check-input"
              role="switch"
              style={{ transform: 'scale(1.3)' }}
              checked={settings.nativeAuthEnabled}
              disabled={saving}
              onChange={e => runUpdate({ nativeAuthEnabled: e.target.checked })}
            />
          </div>
        </div>
      </div>

      )}

      {activeTab === 'accounts' && (
      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <Mail size={18} /> Email (SMTP){' '}
          <span className={`badge ${smtp?.smtpConfigured ? 'text-bg-success' : 'text-bg-secondary'}`}>
            {smtp?.smtpConfigured ? 'Configured' : 'Not configured'}
          </span>
        </h5>
        <p className="text-muted small mb-3">
          Outbound email for signup verification codes. The password is write-only
          (stored server-side, never sent back). The From address may differ from the
          auth username — relay setups authenticate as one mailbox but send as another.
        </p>

        {smtp === null ? (
          <div className="text-muted small d-flex align-items-center gap-2">
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <label htmlFor="smtp-enabled" className="form-check-label mb-0">
                <span className="fw-semibold">Enable email sending</span>
                <span className="form-text d-block mt-0">
                  When off, signup falls back to Keycloak&apos;s emailed verification link.
                </span>
              </label>
              <div className="form-check form-switch m-0">
                <input id="smtp-enabled" type="checkbox" className="form-check-input" role="switch"
                  style={{ transform: 'scale(1.3)' }} checked={!!smtpDraft.smtpEnabled} disabled={smtpSaving}
                  onChange={e => setSmtpField('smtpEnabled', e.target.checked)} />
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-12 col-md-8">
                <label htmlFor="smtp-host" className="fw-semibold small d-block mb-1">Host</label>
                <input id="smtp-host" className="form-control" placeholder="smtp.office365.com"
                  value={smtpDraft.smtpHost ?? ''} disabled={smtpSaving}
                  onChange={e => setSmtpField('smtpHost', e.target.value)} />
              </div>
              <div className="col-6 col-md-2">
                <label htmlFor="smtp-port" className="fw-semibold small d-block mb-1">Port</label>
                <input id="smtp-port" type="number" className="form-control" placeholder="587"
                  value={smtpDraft.smtpPort ?? 587} disabled={smtpSaving}
                  onChange={e => setSmtpField('smtpPort', Number(e.target.value))} />
              </div>
              <div className="col-6 col-md-2 d-flex align-items-end pb-2">
                <div className="form-check form-switch m-0">
                  <input id="smtp-tls" type="checkbox" className="form-check-input" role="switch"
                    checked={smtpDraft.smtpStartTls ?? true} disabled={smtpSaving}
                    onChange={e => setSmtpField('smtpStartTls', e.target.checked)} />
                  <label htmlFor="smtp-tls" className="form-check-label small">STARTTLS</label>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6">
                <label htmlFor="smtp-from" className="fw-semibold small d-block mb-1">From address</label>
                <input id="smtp-from" type="email" className="form-control" placeholder="estimator@trusscore.com"
                  value={smtpDraft.smtpFrom ?? ''} disabled={smtpSaving}
                  onChange={e => setSmtpField('smtpFrom', e.target.value)} />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="smtp-fromname" className="fw-semibold small d-block mb-1">From name</label>
                <input id="smtp-fromname" className="form-control" placeholder="Trusscore"
                  value={smtpDraft.smtpFromName ?? ''} disabled={smtpSaving}
                  onChange={e => setSmtpField('smtpFromName', e.target.value)} />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="smtp-replyto" className="fw-semibold small d-block mb-1">Reply-To <span className="text-muted fw-normal">(optional)</span></label>
                <input id="smtp-replyto" type="email" className="form-control" placeholder="e.g. support@trusscore.com — or leave blank"
                  value={smtpDraft.smtpReplyTo ?? ''} disabled={smtpSaving}
                  onChange={e => setSmtpField('smtpReplyTo', e.target.value)} />
                <div className="form-text">Where replies go if someone answers. Leave blank for none.</div>
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="smtp-notice" className="fw-semibold small d-block mb-1">&ldquo;Do not reply&rdquo; notice</label>
              <textarea id="smtp-notice" className="form-control" rows={2}
                placeholder="Please do not reply to this email — this inbox is not monitored."
                value={smtpDraft.smtpNoReplyNotice ?? ''} disabled={smtpSaving}
                onChange={e => setSmtpField('smtpNoReplyNotice', e.target.value)} />
              <div className="form-text">Appended to the footer of every email the app sends. Leave blank to omit.</div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6">
                <label htmlFor="smtp-user" className="fw-semibold small d-block mb-1">Auth username</label>
                <input id="smtp-user" className="form-control" autoComplete="off" placeholder="mswrelay@mswplastics.ca"
                  value={smtpDraft.smtpUser ?? ''} disabled={smtpSaving}
                  onChange={e => setSmtpField('smtpUser', e.target.value)} />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="smtp-pass" className="fw-semibold small d-block mb-1">
                  Password{' '}
                  <span className={`badge ${smtp.smtpPasswordSet ? 'text-bg-success' : 'text-bg-secondary'}`}>
                    {smtp.smtpPasswordSet ? 'Configured' : 'Not set'}
                  </span>
                </label>
                <input id="smtp-pass" type="password" className="form-control" autoComplete="off"
                  placeholder={smtp.smtpPasswordSet ? 'Enter a new password to replace the stored one' : 'SMTP password'}
                  value={smtpPasswordDraft} disabled={smtpSaving}
                  onChange={e => setSmtpPasswordDraft(e.target.value)} />
              </div>
            </div>

            {smtpMsg && (
              <div className={`alert ${smtpMsg.type === 'ok' ? 'alert-success' : 'alert-danger'} py-2 small`}>
                {smtpMsg.text}
              </div>
            )}

            <div className="d-flex flex-wrap gap-2 align-items-center">
              <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-2"
                disabled={smtpSaving} onClick={saveSmtp}>
                {smtpSaving && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                Save email settings
              </button>
              <div className="input-group" style={{ maxWidth: 380 }}>
                <input type="email" className="form-control" placeholder="Send a test to…"
                  value={smtpTestTo} disabled={smtpTesting}
                  onChange={e => setSmtpTestTo(e.target.value)} />
                <button type="button" className="btn btn-outline-secondary"
                  disabled={smtpTesting || !smtpTestTo.trim()} onClick={testSmtp}>
                  {smtpTesting ? 'Sending…' : 'Send test'}
                </button>
              </div>
            </div>
            <div className="form-text mt-2">
              The test uses the fields above (including an unsaved password), so you can verify before saving.
            </div>
          </>
        )}
      </div>

      )}

      {/* ===== Estimator & Display (cont.) ===== */}
      {activeTab === 'display' && (
      <div className="settings-group border rounded-3 p-3 mb-3">
        <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
          <Video size={18} /> Tutorial Video
        </h5>
        <p className="text-muted small mb-3">
          A YouTube tutorial that opens in a centered lightbox when the user clicks
          either the link under the main estimator title or the "Tutorial Video"
          item in the navigation menu. Leave the URL blank to hide both entry
          points.
        </p>

        <div className="mb-3">
          <label htmlFor="tutorial-url" className="fw-semibold small d-block mb-1">
            YouTube URL
          </label>
          <input
            id="tutorial-url"
            type="url"
            className="form-control"
            placeholder="https://www.youtube.com/watch?v=..."
            value={tutorialUrlDraft}
            disabled={saving}
            onChange={e => setTutorialUrlDraft(e.target.value)}
            onBlur={commitTutorialUrl}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.currentTarget as HTMLInputElement).blur();
              }
            }}
          />
          <div className="form-text">
            Paste a regular YouTube watch URL (or short youtu.be link) — the
            lightbox extracts the video ID and embeds it.
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-12 col-md-6">
            <label htmlFor="tutorial-main-text" className="fw-semibold small d-block mb-1">
              Main UI link text
            </label>
            <input
              id="tutorial-main-text"
              type="text"
              className="form-control"
              placeholder={TUTORIAL_DEFAULTS_EN.main}
              value={tutorialMainTextDraft}
              disabled={saving}
              onChange={e => setTutorialMainTextDraft(e.target.value)}
              onBlur={commitTutorialMainText}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.currentTarget as HTMLInputElement).blur();
                }
              }}
            />
            <div className="form-text">Shown under "Choose your project type to get started".</div>
          </div>
          <div className="col-12 col-md-6">
            <label htmlFor="tutorial-menu-text" className="fw-semibold small d-block mb-1">
              Menu item label
            </label>
            <input
              id="tutorial-menu-text"
              type="text"
              className="form-control"
              placeholder={TUTORIAL_DEFAULTS_EN.menu}
              value={tutorialMenuTextDraft}
              disabled={saving}
              onChange={e => setTutorialMenuTextDraft(e.target.value)}
              onBlur={commitTutorialMenuText}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.currentTarget as HTMLInputElement).blur();
                }
              }}
            />
            <div className="form-text">Shown in the header dropdown and mobile nav.</div>
          </div>
        </div>

        <BackdropEditor
          color={settings.tutorialBackdropColor}
          blurPx={settings.tutorialBackdropBlurPx}
          disabled={saving}
          onColorChange={next => runUpdate({ tutorialBackdropColor: next })}
          onBlurChange={next => runUpdate({ tutorialBackdropBlurPx: next })}
        />
      </div>

      )}

      {/* ===== General (cont.) ===== */}
      {activeTab === 'general' && (
      <RequiredAcronymsEditor
        value={settings.requiredAcronyms}
        disabled={saving}
        onChange={next => runUpdate({ requiredAcronyms: next })}
      />
      )}
    </div>
  );
};

// ============================================================================
// Required-acronyms editor — chip list + add input
// ============================================================================

interface AcronymsEditorProps {
  value: string[];
  disabled: boolean;
  onChange: (next: string[]) => void;
}

const RequiredAcronymsEditor: React.FC<AcronymsEditorProps> = ({ value, disabled, onChange }) => {
  const [draft, setDraft] = useState('');

  const add = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed.length > 20) return;
    if (value.includes(trimmed)) { setDraft(''); return; }
    onChange([...value, trimmed]);
    setDraft('');
  };

  const remove = (acronym: string) => {
    onChange(value.filter(a => a !== acronym));
  };

  return (
    <div className="settings-group border rounded-3 p-3 mb-3">
      <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
        <ShieldCheck size={18} /> Retailer Eligibility — Required Acronyms
      </h5>
      <p className="text-muted small mb-3">
        A retailer must carry at least one <em>active</em> product with each
        of the acronyms listed below to be eligible for retailer-priced
        estimates. Removing an acronym makes more retailers eligible; adding
        one tightens the requirement. Acronyms must match exactly what's in
        the products table (e.g. <code>W&amp;CB</code>, <code>SW</code>,
        <code>J1/2</code>).
      </p>

      <div className="d-flex flex-wrap gap-1 mb-3">
        {value.length === 0 ? (
          <em className="text-muted small">No acronyms required — every retailer is eligible.</em>
        ) : (
          value.map(a => (
            <span
              key={a}
              className="badge text-bg-light border d-inline-flex align-items-center gap-1"
              style={{ fontSize: '0.85em', padding: '6px 10px' }}
            >
              <code className="text-dark">{a}</code>
              <button
                type="button"
                className="btn btn-sm btn-link p-0 text-danger"
                aria-label={`Remove ${a}`}
                style={{ lineHeight: 1 }}
                disabled={disabled}
                onClick={() => remove(a)}
              >
                <X size={14} />
              </button>
            </span>
          ))
        )}
      </div>

      <div className="input-group input-group-sm" style={{ maxWidth: 320 }}>
        <input
          type="text"
          className="form-control"
          placeholder="Add acronym (e.g. W&CB)"
          value={draft}
          maxLength={20}
          disabled={disabled}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-outline-primary d-inline-flex align-items-center gap-1"
          disabled={disabled || !draft.trim()}
          onClick={add}
        >
          <Plus size={14} /> Add
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Backdrop editor — RGBA pickers + blur slider for the tutorial lightbox
// ============================================================================

interface BackdropEditorProps {
  color: { r: number; g: number; b: number; a: number };
  blurPx: number;
  disabled: boolean;
  onColorChange: (next: { r: number; g: number; b: number; a: number }) => void;
  onBlurChange: (next: number) => void;
}

const toHex = (n: number): string => {
  const v = Math.max(0, Math.min(255, Math.round(n)));
  return v.toString(16).padStart(2, '0');
};

const rgbToHex = (c: { r: number; g: number; b: number }): string =>
  `#${toHex(c.r)}${toHex(c.g)}${toHex(c.b)}`;

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const m = /^#?([a-f\d]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const num = parseInt(m[1], 16);
  return { r: (num >> 16) & 0xff, g: (num >> 8) & 0xff, b: num & 0xff };
};

const BackdropEditor: React.FC<BackdropEditorProps> = ({
  color, blurPx, disabled, onColorChange, onBlurChange,
}) => {
  // Local draft state — typing in R/G/B/A numbers and dragging the blur
  // slider only updates the draft. Server writes happen on blur (numbers)
  // or on pointer release (slider) so each keystroke / drag tick doesn't
  // round-trip to /api/settings.php and thrash the input.
  const [rDraft, setRDraft] = useState(String(color.r));
  const [gDraft, setGDraft] = useState(String(color.g));
  const [bDraft, setBDraft] = useState(String(color.b));
  const [aDraft, setADraft] = useState(String(color.a));
  const [blurDraft, setBlurDraft] = useState(blurPx);

  // Keep drafts in sync when the server-owned values change (initial
  // hydration, restore-defaults, edits from another tab).
  useEffect(() => { setRDraft(String(color.r)); }, [color.r]);
  useEffect(() => { setGDraft(String(color.g)); }, [color.g]);
  useEffect(() => { setBDraft(String(color.b)); }, [color.b]);
  useEffect(() => { setADraft(String(color.a)); }, [color.a]);
  useEffect(() => { setBlurDraft(blurPx); }, [blurPx]);

  // Live preview reflects the draft, not the saved value, so users see
  // their edits before committing.
  const previewR = clampInt(rDraft, 0, 255, color.r);
  const previewG = clampInt(gDraft, 0, 255, color.g);
  const previewB = clampInt(bDraft, 0, 255, color.b);
  const previewA = clampFloat(aDraft, 0, 1, color.a);
  const rgbaPreview = `rgba(${previewR}, ${previewG}, ${previewB}, ${previewA.toFixed(2)})`;
  const hex = rgbToHex({ r: previewR, g: previewG, b: previewB });

  const commitChannel = (key: 'r' | 'g' | 'b' | 'a') => {
    const next = { ...color };
    if (key === 'a') {
      const v = clampFloat(aDraft, 0, 1, color.a);
      next.a = v;
      setADraft(String(v));
    } else {
      const draft = key === 'r' ? rDraft : key === 'g' ? gDraft : bDraft;
      const v = clampInt(draft, 0, 255, color[key]);
      next[key] = v;
      const setter = key === 'r' ? setRDraft : key === 'g' ? setGDraft : setBDraft;
      setter(String(v));
    }
    if (next.r === color.r && next.g === color.g && next.b === color.b && next.a === color.a) return;
    onColorChange(next);
  };

  const commitBlur = () => {
    const v = Math.max(0, Math.min(50, Math.round(blurDraft)));
    if (v === blurPx) return;
    onBlurChange(v);
  };

  const numberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.currentTarget as HTMLInputElement).blur();
    }
  };

  return (
    <div className="border rounded-3 p-3">
      <div className="fw-semibold small mb-2">Lightbox backdrop</div>

      <div className="row g-3 align-items-end">
        <div className="col-12 col-md-3">
          <label className="form-label small mb-1">Colour</label>
          <input
            type="color"
            className="form-control form-control-color w-100"
            value={hex}
            disabled={disabled}
            onChange={e => {
              // Native colour picker — single deliberate action, fine to
              // commit on change. Keeps the existing alpha.
              const rgb = hexToRgb(e.target.value);
              if (rgb) onColorChange({ ...rgb, a: color.a });
            }}
          />
        </div>

        <div className="col-6 col-md-1">
          <label className="form-label small mb-1">R</label>
          <input
            type="number"
            className="form-control form-control-sm"
            min={0}
            max={255}
            value={rDraft}
            disabled={disabled}
            onChange={e => setRDraft(e.target.value)}
            onBlur={() => commitChannel('r')}
            onKeyDown={numberKeyDown}
          />
        </div>
        <div className="col-6 col-md-1">
          <label className="form-label small mb-1">G</label>
          <input
            type="number"
            className="form-control form-control-sm"
            min={0}
            max={255}
            value={gDraft}
            disabled={disabled}
            onChange={e => setGDraft(e.target.value)}
            onBlur={() => commitChannel('g')}
            onKeyDown={numberKeyDown}
          />
        </div>
        <div className="col-6 col-md-1">
          <label className="form-label small mb-1">B</label>
          <input
            type="number"
            className="form-control form-control-sm"
            min={0}
            max={255}
            value={bDraft}
            disabled={disabled}
            onChange={e => setBDraft(e.target.value)}
            onBlur={() => commitChannel('b')}
            onKeyDown={numberKeyDown}
          />
        </div>
        <div className="col-6 col-md-2">
          <label className="form-label small mb-1">Opacity (A)</label>
          <input
            type="number"
            className="form-control form-control-sm"
            min={0}
            max={1}
            step={0.05}
            value={aDraft}
            disabled={disabled}
            onChange={e => setADraft(e.target.value)}
            onBlur={() => commitChannel('a')}
            onKeyDown={numberKeyDown}
          />
        </div>

        <div className="col-12 col-md-4">
          <label className="form-label small mb-1">Preview</label>
          <div
            className="border rounded"
            style={{
              height: 38,
              background:
                `linear-gradient(45deg, #f3f3f3 25%, transparent 25%, transparent 75%, #f3f3f3 75%), ` +
                `linear-gradient(45deg, #f3f3f3 25%, transparent 25%, transparent 75%, #f3f3f3 75%) #fff`,
              backgroundSize: '12px 12px',
              backgroundPosition: '0 0, 6px 6px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{ position: 'absolute', inset: 0, background: rgbaPreview }} />
          </div>
        </div>
      </div>

      <div className="mt-3">
        <div className="d-flex align-items-center justify-content-between mb-1">
          <label htmlFor="tutorial-blur" className="fw-semibold small m-0">
            Blur amount behind backdrop
          </label>
          <span className="badge text-bg-light" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {blurDraft}px
          </span>
        </div>
        <input
          id="tutorial-blur"
          type="range"
          className="form-range"
          min={0}
          max={50}
          step={1}
          value={blurDraft}
          disabled={disabled}
          // Live preview while dragging — commit only on release so we don't
          // fire a server save on every tick.
          onChange={e => setBlurDraft(Math.round(Number(e.target.value)))}
          onMouseUp={commitBlur}
          onTouchEnd={commitBlur}
          onKeyUp={commitBlur}
        />
        <div className="form-text">
          Blur applied to the page behind the backdrop. <code>0</code> disables the effect.
        </div>
      </div>
    </div>
  );
};

function clampInt(raw: string, min: number, max: number, fallback: number): number {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return fallback;
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

function clampFloat(raw: string, min: number, max: number, fallback: number): number {
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return fallback;
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

export default AdminSettings;
